import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

// COMPONENTS
import CountrySelect from '../../common/inputs/CountrySelect';
import ErrorList from '../../common/ErrorList';
import PasswordRequirements from '../../common/PasswordRequirements';

// MESSAGES
import formattedMessages from '../../../messages/account/AdminFormattedMessages';

// REQUESTS
import useAdministratorRequest from '../../../requests/AdministratorRequest';

// UTILS
import {
  validateEmail,
  validateName,
  validatePassword,
  validatePhoneNumber,
} from '../../../utils/validate';

const AdministratorEditForm = ({ administrator, onClose, errorHandler }) => {
  const { editAdministrator, createAdministrator, getExtras, getUserByEmail } =
    useAdministratorRequest();

  const [formData, setFormData] = useState({
    contact_name: administrator?.contact_name || '',
    contact_email: administrator?.contact_email || '',
    contact_posn: administrator?.contact_posn || '',
    contact_dept: administrator?.contact_dept || '',
    contact_tn: administrator?.contact_tn || '',
    contact_mobile: administrator?.contact_mobile || '',
    contact_address1: administrator?.contact_address1 || '',
    contact_address2: administrator?.contact_address2 || '',
    contact_city: administrator?.contact_city || '',
    contact_state: administrator?.contact_state || '',
    contact_postcode: administrator?.contact_postcode || '',
    country_id: administrator?.country_id || '',
    password: '',
    password_check: '',
    access_level: administrator?.access_level || '',
    billing_contact: administrator?.billing_contact || 'N',
    sales_contact: administrator?.sales_contact || 'N',
    technical_contact: administrator?.technical_contact || 'N',
  });

  const [accessLevels, setAccessLevels] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [generalError, setGeneralError] = useState('');

  useEffect(() => {
    const loadExtras = async () => {
      try {
        const extras = await getExtras(errorHandler);
        if (extras?.access_levels) {
          setAccessLevels(extras.access_levels);
        }
      } catch (error) {
        console.error('Error loading extras:', error);
        setGeneralError('Error loading form data');
      }
    };

    loadExtras();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      delete errors[name];
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!validateName(formData.contact_name)) {
      newErrors.contact_name =
        'Name is required and must contain only letters, spaces, and hyphens';
    }

    if (!administrator) {
      if (!formData.password || !formData.password_check) {
        newErrors.password = 'Password is required for new administrators';
      } else if (!validatePassword(formData.password)) {
        newErrors.password =
          'Password must be at least 8 characters and contain uppercase, lowercase, number, and symbol';
      }

      if (formData.password !== formData.password_check) {
        newErrors.password_check = 'Passwords do not match';
      }
    } else if (formData.password) {
      if (!validatePassword(formData.password)) {
        newErrors.password =
          'Password must be at least 8 characters and contain uppercase, lowercase, number, and symbol';
      }
      if (formData.password !== formData.password_check) {
        newErrors.password_check = 'Passwords do not match';
      }
    }

    if (!formData.contact_tn) {
      newErrors.contact_tn = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.contact_tn)) {
      newErrors.contact_tn =
        'Invalid phone number format. Use only numbers and + symbol';
    }

    if (
      formData.contact_mobile &&
      !validatePhoneNumber(formData.contact_mobile)
    ) {
      newErrors.contact_mobile =
        'Invalid mobile number format. Use only numbers and + symbol';
    }

    if (!formData.contact_email) {
      newErrors.contact_email = 'Email is required';
    } else if (!validateEmail(formData.contact_email)) {
      newErrors.contact_email = 'Invalid email format';
    }

    if (!formData.country_id) {
      newErrors.country_id = 'Country selection is required';
    }

    if (!formData.access_level) {
      newErrors.access_level = 'Access level selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (!administrator) {
        // Check if user/contact exists for new administrators
        const { user, contact } = await getUserByEmail(
          formData.contact_email,
          errorHandler
        );

        if (contact) {
          setGeneralError(formattedMessages.errContactExists);
          setIsSubmitting(false);
          return;
        }

        if (user === 'Other Customer') {
          setGeneralError(formattedMessages.errUserExists);
          setIsSubmitting(false);
          return;
        }

        if (user) {
          setShowUpgradeModal(true);
          setIsSubmitting(false);
          return;
        }
      }

      await submitForm();
    } catch (error) {
      console.error('Error submitting form:', error);
      setGeneralError('Error saving administrator');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitForm = async () => {
    try {
      if (administrator) {
        await editAdministrator(
          administrator.customer_contact_id,
          formData,
          errorHandler
        );
      } else {
        await createAdministrator(formData, errorHandler);
      }
      onClose(true);
    } catch (error) {
      console.error('Error in form submission:', error);
      setGeneralError('Failed to save administrator');
    }
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {administrator && (
            <Typography variant="h3" gutterBottom>
              {administrator.contact_email}
            </Typography>
          )}

          <Typography variant="h2" gutterBottom>
            CONTACT INFORMATION
          </Typography>

          {/* Two-column layout for contact information */}
          <Grid container columnSpacing={6}>
            {/* Left column - main contact info */}
            <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 450 }}>
              {/* Name Field */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  required
                  sx={{
                    width: 170,
                  }}
                >
                  Name
                </InputLabel>
                <TextField
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleInputChange}
                  error={!!errors.contact_name}
                  helperText={errors.contact_name}
                  size="small"
                  fullWidth
                />
              </Stack>

              {!administrator && (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ md: 'center' }}
                  marginBottom={2}
                >
                  <InputLabel
                    required
                    sx={{
                      width: 170,
                    }}
                  >
                    Email
                  </InputLabel>
                  <TextField
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    error={!!errors.contact_email}
                    helperText={errors.contact_email}
                    size="small"
                    fullWidth
                  />
                </Stack>
              )}

              {/* Phone Field */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  required
                  sx={{
                    width: 170,
                  }}
                >
                  Phone
                </InputLabel>
                <TextField
                  name="contact_tn"
                  value={formData.contact_tn}
                  onChange={handleInputChange}
                  error={!!errors.contact_tn}
                  helperText={errors.contact_tn}
                  size="small"
                  fullWidth
                />
              </Stack>

              {/* Access Level Field */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  required
                  sx={{
                    width: 170,
                  }}
                >
                  Access Level
                </InputLabel>
                <FormControl error={!!errors.access_level} fullWidth>
                  <Select
                    name="access_level"
                    value={formData.access_level}
                    onChange={handleInputChange}
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Access Level
                    </MenuItem>
                    {accessLevels.map((level) => (
                      <MenuItem key={level.code} value={level.code}>
                        {level.meaning}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.access_level && (
                    <FormHelperText error>{errors.access_level}</FormHelperText>
                  )}
                </FormControl>
              </Stack>
            </Grid>

            {/* Right column - position and department */}
            <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 450 }}>
              {/* Position Field */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  sx={{
                    width: 170,
                  }}
                >
                  Position
                </InputLabel>
                <TextField
                  name="contact_posn"
                  value={formData.contact_posn}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Stack>

              {/* Department Field */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  sx={{
                    width: 170,
                  }}
                >
                  Department
                </InputLabel>
                <TextField
                  name="contact_dept"
                  value={formData.contact_dept}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Stack>
            </Grid>
          </Grid>

          {/* Contact Types Section */}
          <Box>
            <InputLabel
              sx={{
                width: 170,
                marginBottom: 2,
              }}
            >
              Contact Type
            </InputLabel>

            {/* Billing Contact */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
              sx={{ mb: 0.5 }}
            >
              <InputLabel
                sx={{
                  width: 130,
                  pl: 2,
                }}
              >
                Billing
              </InputLabel>
              <Box sx={{ width: '300px' }}>
                <RadioGroup
                  row
                  name="billing_contact"
                  value={formData.billing_contact}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="P"
                    control={<Radio />}
                    label="Primary"
                    sx={{ margin: 0 }}
                  />
                  <FormControlLabel
                    value="S"
                    control={<Radio />}
                    label="Secondary"
                    sx={{ margin: 0 }}
                  />
                  <FormControlLabel
                    value="N"
                    control={<Radio />}
                    label="N/A"
                    sx={{ margin: 0 }}
                  />
                </RadioGroup>
              </Box>
            </Stack>

            {/* Sales Contact */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
              sx={{ mb: 0.5 }}
            >
              <InputLabel
                sx={{
                  width: 130,
                  pl: 2,
                }}
              >
                Sales
              </InputLabel>
              <Box sx={{ width: '300px' }}>
                <RadioGroup
                  row
                  name="sales_contact"
                  value={formData.sales_contact}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="P"
                    control={<Radio />}
                    label="Primary"
                    sx={{ margin: 0 }}
                  />
                  <FormControlLabel
                    value="S"
                    control={<Radio />}
                    label="Secondary"
                    sx={{ margin: 0 }}
                  />
                  <FormControlLabel
                    value="N"
                    control={<Radio />}
                    label="N/A"
                    sx={{ margin: 0 }}
                  />
                </RadioGroup>
              </Box>
            </Stack>

            {/* Technical Contact */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
              sx={{ mb: 0.5 }}
            >
              <InputLabel
                sx={{
                  width: 130,
                  pl: 2,
                }}
              >
                Technical
              </InputLabel>
              <Box sx={{ width: '300px' }}>
                <RadioGroup
                  row
                  name="technical_contact"
                  value={formData.technical_contact}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="P"
                    control={<Radio />}
                    label="Primary"
                    sx={{ margin: 0 }}
                  />
                  <FormControlLabel
                    value="S"
                    control={<Radio />}
                    label="Secondary"
                    sx={{ margin: 0 }}
                  />
                  <FormControlLabel
                    value="N"
                    control={<Radio />}
                    label="N/A"
                    sx={{ margin: 0 }}
                  />
                </RadioGroup>
              </Box>
            </Stack>
          </Box>

          {/* Password Section */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 450 }}>
              {/* Password Field */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  required={!administrator}
                  sx={{
                    width: 170,
                  }}
                >
                  Password
                </InputLabel>
                <TextField
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  size="small"
                  fullWidth
                />
              </Stack>

              {/* Confirm Password Field */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  required={!administrator}
                  sx={{
                    width: 170,
                  }}
                >
                  Confirm Password
                </InputLabel>
                <TextField
                  type="password"
                  name="password_check"
                  value={formData.password_check}
                  onChange={handleInputChange}
                  error={!!errors.password_check}
                  helperText={errors.password_check}
                  size="small"
                  fullWidth
                />
              </Stack>
            </Grid>

            {/* Password Requirements */}
            <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 220 }}>
              <PasswordRequirements />
            </Grid>
          </Grid>

          {/* Company Information Section */}
          <Typography variant="h2" gutterBottom sx={{ paddingTop: 6 }}>
            COMPANY INFORMATION
          </Typography>

          <Grid container columnSpacing={6}>
            {/* Left column - main contact info */}
            <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 450 }}>
              {/* Street Address */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  sx={{
                    width: 170,
                  }}
                >
                  Street Address
                </InputLabel>
                <TextField
                  name="contact_address1"
                  value={formData.contact_address1}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Stack>

              {/* Continued Address */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  sx={{
                    width: 170,
                  }}
                >
                  Continued
                </InputLabel>
                <TextField
                  name="contact_address2"
                  value={formData.contact_address2}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Stack>

              {/* City */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  sx={{
                    width: 170,
                  }}
                >
                  City
                </InputLabel>
                <TextField
                  name="contact_city"
                  value={formData.contact_city}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Stack>

              {/* State */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  sx={{
                    width: 170,
                  }}
                >
                  State
                </InputLabel>
                <TextField
                  name="contact_state"
                  value={formData.contact_state}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Stack>

              {/* Country */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
                marginBottom={2}
              >
                <InputLabel
                  required
                  sx={{
                    width: 170,
                  }}
                >
                  Country
                </InputLabel>
                <FormControl error={!!errors.country_id} fullWidth>
                  <CountrySelect
                    value={formData.country_id}
                    name="country_id"
                    fullWidth
                    selectDefault
                    onChange={(_, newValue) =>
                      handleInputChange({
                        target: {
                          name: 'country_id',
                          value: newValue,
                        },
                      })
                    }
                    error={!!errors.country_id}
                    helperText={errors.country_id}
                    required
                    errorHandler={errorHandler}
                  />
                </FormControl>
              </Stack>

              {/* Error Summary */}
              {errors && Object.keys(errors).length > 0 && (
                <ErrorList errors={Object.values(errors)} />
              )}

              {/* General Error Display */}
              {generalError && <Alert severity="error">{generalError}</Alert>}

              {/* Action Buttons */}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="end"
                sx={{ marginY: 4 }}
              >
                <Button
                  variant="link"
                  color="secondary"
                  onClick={() => onClose(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {/* Upgrade User Modal */}
      <Dialog
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      >
        <DialogTitle variant="h2">{formattedMessages.modalTitle}</DialogTitle>
        <DialogContent>{formattedMessages.modalBody}</DialogContent>
        <DialogActions>
          <Button onClick={submitForm} color="secondary">
            Yes
          </Button>
          <Button onClick={() => setShowUpgradeModal(false)} color="secondary">
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdministratorEditForm;
